import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import MapGL, { Layer, Marker, Source } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Box } from '@mui/material'
import { AGE, C, TONE } from './tokens'
import { ageLevel, initials, isMoving } from './model'

/**
 * Split-view map: every live, non-stale order (customer pin colored by age),
 * the restaurants they come from, and the drivers carrying them. Hovering a
 * pin highlights its row and the reverse; clicking a pin opens the order.
 */
export default function DispatchMap({ views, now, mapboxToken, hoveredId, onHover, onSelect }) {
  const mapRef = useRef(null)
  const fitted = useRef(false)

  const restaurants = useMemo(() => {
    const byId = new Map()
    views.forEach(v => { if (v.restaurant.location && !byId.has(v.restaurant.id)) byId.set(v.restaurant.id, v.restaurant) })
    return [...byId.values()]
  }, [views])

  const drivers = useMemo(
    () => views.filter(v => v.driver?.location).map(v => ({ key: `${v.id}-drv`, view: v, at: v.driver.location, moving: isMoving(v) })),
    [views],
  )

  const lines = useMemo(() => ({
    type: 'FeatureCollection',
    features: views
      .filter(v => v.restaurant.location && v.address?.location && (isMoving(v) || v.id === hoveredId))
      .map(v => ({
        type: 'Feature',
        properties: { moving: isMoving(v) ? 1 : 0 },
        geometry: { type: 'LineString', coordinates: [[v.restaurant.location.lng, v.restaurant.location.lat], [v.address.location.lng, v.address.location.lat]] },
      })),
  }), [views, hoveredId])

  const fit = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const pts = [
      ...restaurants.map(r => r.location),
      ...views.map(v => v.address?.location).filter(Boolean),
      ...drivers.map(d => d.at),
    ]
    if (pts.length === 0) return
    if (pts.length === 1) {
      map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 12 })
      return
    }
    const lngs = pts.map(p => p.lng)
    const lats = pts.map(p => p.lat)
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 48, duration: 500, maxZoom: 14 })
  }, [restaurants, views, drivers])

  // Fit once when there's something to show; after that the dispatcher owns the camera.
  useEffect(() => {
    if (!fitted.current && views.length > 0 && mapRef.current) {
      fit()
      fitted.current = true
    }
  }, [views.length, fit])

  if (!mapboxToken) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: C.mutedBg, fontSize: 13, color: C.muted, textAlign: 'center', px: 3 }}>
        No Mapbox token — set one in Configuration to see the live map.
      </Box>
    )
  }

  const start = restaurants[0]?.location || { lat: 42.52, lng: -70.93 }
  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <MapGL
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        initialViewState={{ longitude: start.lng, latitude: start.lat, zoom: 11 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        onLoad={() => {
          fit()
          fitted.current = views.length > 0
        }}
      >
        {/* Two layers: line-dasharray can't vary per feature. */}
        <Source id="dispatch-lines" type="geojson" data={lines}>
          <Layer
            id="dispatch-lines-moving"
            type="line"
            filter={['==', ['get', 'moving'], 1]}
            layout={{ 'line-cap': 'round' }}
            paint={{ 'line-color': TONE.transit.dot, 'line-width': 2.5, 'line-opacity': 0.75 }}
          />
          <Layer
            id="dispatch-lines-planned"
            type="line"
            filter={['==', ['get', 'moving'], 0]}
            paint={{ 'line-color': C.text, 'line-width': 2, 'line-opacity': 0.55, 'line-dasharray': [2, 2] }}
          />
        </Source>

        {restaurants.map(r => (
          <Marker key={r.id} longitude={r.location.lng} latitude={r.location.lat} anchor="center">
            <div
              title={r.name}
              style={{
                width: 22, height: 22, borderRadius: 6, background: C.ink, color: '#fff', border: '2px solid #fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700,
              }}
            >
              {initials(r.name)}
            </div>
          </Marker>
        ))}

        {views.filter(v => v.address?.location).map(v => {
          const hot = hoveredId === v.id
          const color = AGE[ageLevel(v, now)]?.bar || C.faint
          return (
            <Marker
              key={v.id}
              longitude={v.address.location.lng}
              latitude={v.address.location.lat}
              anchor="center"
              style={{ zIndex: hot ? 3 : 1 }}
              onClick={e => {
                e.originalEvent.stopPropagation()
                onSelect(v.id)
              }}
            >
              <div
                title={`#${v.orderNo}${v.customer ? ` · ${v.customer.name}` : ''}`}
                onMouseEnter={() => onHover(v.id)}
                onMouseLeave={() => onHover(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              >
                <div style={{ width: hot ? 16 : 12, height: hot ? 16 : 12, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.35)', transition: 'width 120ms, height 120ms' }} />
                {hot && (
                  <div style={{ padding: '2px 6px', borderRadius: 6, background: C.ink, color: '#fff', fontSize: 11, fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    #{v.orderNo}
                  </div>
                )}
              </div>
            </Marker>
          )
        })}

        {drivers.map(d => (
          <Marker key={d.key} longitude={d.at.lng} latitude={d.at.lat} anchor="center" style={{ zIndex: 2 }}>
            <div
              title={`${d.view.driver.name} · #${d.view.orderNo}`}
              style={{ width: 14, height: 14, borderRadius: '50%', background: d.moving ? TONE.transit.dot : C.brandGreen, border: '2.5px solid #fff', boxShadow: '0 0 0 4px rgba(14,116,144,0.18), 0 1px 3px rgba(0,0,0,0.35)' }}
            />
          </Marker>
        ))}
      </MapGL>
    </Box>
  )
}

export function MapLegend() {
  const dot = color => ({ width: 10, height: 10, borderRadius: '50%', bgcolor: color, border: `1px solid ${C.card}` })
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.5, fontSize: 11, color: C.muted }}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: C.ink }} /> Restaurant</Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ display: 'flex' }}><Box sx={dot(AGE.fresh.bar)} /><Box sx={{ ...dot(AGE.warn.bar), ml: '-2px' }} /><Box sx={{ ...dot(AGE.late.bar), ml: '-2px' }} /></Box>
        Customer, by age
      </Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><Box sx={dot(TONE.transit.dot)} /> Driver on the road</Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><Box sx={dot(C.brandGreen)} /> Driver assigned</Box>
    </Box>
  )
}
